import React, {
    useEffect,
    useState
} from "react";

import {
    ArrowLeft,
    Printer,
    RefreshCw,
    Truck,
    AlertTriangle
} from "lucide-react";

import {
    getDeliveryById
} from "../services/api";

import {
    useNavigate,
    useParams
} from "react-router-dom";

import "./DeliveryChallanView.css";


const COMPANY_NAME =
    "Bhimashankar Agro Coldchain & Processing Producer Co. Ltd.";

const COMPANY_ADDRESS =
    "Add. Gat No. 628, A/p- Pargaon (Peth), Tal. Ambegaon, Dist. Pune - 410 512";

const COMPANY_MOBILE =
    "Mob. 9822974495 / 9921366271 / 9921252022";

const COMPANY_EMAIL =
    "bhimashankaragrocoldchain@gmail.com";


const DeliveryChallanView = () => {

    const navigate = useNavigate();

    const {
        id
    } = useParams();


    // =====================================================
    // STATE
    // =====================================================

    const [
        challan,
        setChallan
    ] = useState(null);


    const [
        loading,
        setLoading
    ] = useState(true);


    const [
        error,
        setError
    ] = useState("");


    // =====================================================
    // LOAD CHALLAN
    // =====================================================

    const loadChallan =
        async () => {

            try {

                setLoading(true);

                setError("");


                const response =
                    await getDeliveryById(id);


                const data =
                    response.data?.data ||
                    response.data;


                if (!data) {

                    throw new Error(
                        "Delivery challan information not found."
                    );

                }


                setChallan(data);

            } catch (err) {

                console.error(
                    "Delivery Challan Load Error:",
                    err
                );


                setError(
                    err.response?.data?.message ||
                    err.message ||
                    "Unable to load delivery challan."
                );

            } finally {

                setLoading(false);

            }

        };


    // =====================================================
    // INITIAL LOAD
    // =====================================================

    useEffect(() => {

        loadChallan();

    }, [id]);


    // =====================================================
    // PRINT
    // =====================================================

    const handlePrint = () => {

        window.print();

    };


    // =====================================================
    // FORMAT DATE
    // =====================================================

    const formatDate =
        (date) => {

            if (!date) {

                return "-";

            }


            const value =
                String(date).slice(0, 10);


            const parts =
                value.split("-");


            if (
                parts.length !== 3
            ) {

                return date;

            }


            return `${parts[2]}/${parts[1]}/${parts[0]}`;

        };


    // =====================================================
    // NUMBER FORMAT
    // =====================================================

    const numberFormat =
        (value) => {

            const number =
                Number(value || 0);


            return number.toLocaleString(
                "en-IN"
            );

        };


    // =====================================================
    // NET WEIGHT
    // =====================================================

    const getNetWeight =
        () => {

            if (
                Number(
                    challan?.net_weight_kg
                ) > 0
            ) {

                return Number(
                    challan.net_weight_kg
                );

            }


            const full =
                Number(
                    challan?.full_lorry_weight_kg ||
                    0
                );


            const empty =
                Number(
                    challan?.empty_lorry_weight_kg ||
                    0
                );


            if (
                full > 0 &&
                empty > 0 &&
                full >= empty
            ) {

                return full - empty;

            }


            return 0;

        };


    // =====================================================
    // LOADING
    // =====================================================

    if (loading) {

        return (

            <div className="dc-view-loading">

                <RefreshCw
                    size={32}
                    className="dc-spin"
                />

                <h2>
                    Loading Delivery Challan
                </h2>

                <p>
                    Please wait...
                </p>

            </div>

        );

    }


    // =====================================================
    // ERROR
    // =====================================================

    if (error) {

        return (

            <div className="dc-view-error-page">

                <div className="dc-view-error-card">

                    <AlertTriangle
                        size={42}
                    />

                    <h2>
                        Delivery Challan Unavailable
                    </h2>

                    <p>
                        {error}
                    </p>


                    <div className="dc-error-actions">

                        <button
                            type="button"
                            onClick={() =>
                                navigate(
                                    "/delivery-challans"
                                )
                            }
                        >

                            <ArrowLeft
                                size={18}
                            />

                            Back

                        </button>


                        <button
                            type="button"
                            onClick={
                                loadChallan
                            }
                        >

                            <RefreshCw
                                size={18}
                            />

                            Retry

                        </button>

                    </div>

                </div>

            </div>

        );

    }


    // =====================================================
    // DATA
    // =====================================================

    const items =
        Array.isArray(challan?.items)
            ? challan.items
            : [];


    const totalBags =
        items.reduce(
            (
                total,
                item
            ) =>
                total +
                Number(
                    item.bags_quantity || 0
                ),
            0
        );


    const totalWeight =
        items.reduce(
            (
                total,
                item
            ) =>
                total +
                Number(
                    item.weight_kg || 0
                ),
            0
        );


    const netWeight =
        getNetWeight();


    // =====================================================
    // PAGE
    // =====================================================

    return (

        <div className="dc-view-page">


            {/* =================================================
                TOP ACTION BAR
            ================================================= */}

            <div className="dc-view-toolbar">


                <button
                    type="button"
                    className="dc-toolbar-back"
                    onClick={() =>
                        navigate(
                            "/delivery-challans"
                        )
                    }
                >

                    <ArrowLeft
                        size={19}
                    />

                    Back

                </button>


                <div className="dc-toolbar-right">

                    <button
                        type="button"
                        className="dc-toolbar-refresh"
                        onClick={
                            loadChallan
                        }
                    >

                        <RefreshCw
                            size={18}
                        />

                        Refresh

                    </button>


                    <button
                        type="button"
                        className="dc-toolbar-print"
                        onClick={
                            handlePrint
                        }
                    >

                        <Printer
                            size={19}
                        />

                        Print / Save PDF

                    </button>

                </div>

            </div>


            {/* =================================================
                PRINTABLE DOCUMENT
            ================================================= */}

            <div className="delivery-challan-document">


                {/* =================================================
                    COMPANY HEADER
                ================================================= */}

                <div className="dc-company-header">

                    <h1>
                        {COMPANY_NAME}
                    </h1>


                    <p>
                        {COMPANY_ADDRESS}
                    </p>


                    <p>
                        {COMPANY_MOBILE}
                    </p>

                    <p>
                        {COMPANY_EMAIL}
                    </p>

                </div>


                {/* =================================================
                    DOCUMENT TITLE
                ================================================= */}

                <div className="dc-title-wrapper">

                    <div className="dc-title">

                        DELIVERY CHALLAN
                        <span>
                            (जावक)
                        </span>

                    </div>

                </div>


                {/* =================================================
                    BASIC INFORMATION
                ================================================= */}

                <div className="dc-basic-info">


                    <div className="dc-info-row">

                        <div className="dc-info-item">

                            <span className="dc-label">
                                No.
                            </span>

                            <strong>
                                {challan.challan_no || "-"}
                            </strong>

                        </div>


                        <div className="dc-info-item dc-date">

                            <span className="dc-label">
                                Date
                            </span>

                            <strong>
                                {
                                    formatDate(
                                        challan.challan_date
                                    )
                                }
                            </strong>

                        </div>

                    </div>


                    <div className="dc-single-info">

                        <span className="dc-label">
                            M/s.
                        </span>

                        <strong>
                            {
                                challan.customer_name ||
                                "-"
                            }
                        </strong>

                    </div>


                    <div className="dc-single-info">

                        <span className="dc-label">
                            Vehicle No.
                        </span>

                        <strong>
                            {
                                challan.vehicle_no ||
                                "-"
                            }
                        </strong>

                    </div>


                    <div className="dc-single-info">

                        <span className="dc-label">
                            Driver
                        </span>

                        <strong>

                            {
                                challan.driver_name ||
                                "-"
                            }

                            {
                                challan.driver_mobile
                                    ? ` (${challan.driver_mobile})`
                                    : ""
                            }

                        </strong>

                    </div>

                </div>


                {/* =================================================
                    TABLE INTRO
                ================================================= */}

                <div className="dc-table-intro">

                    Received the above mentioned goods previously
                    stored with you in the same quantity & condition.

                </div>


                {/* =================================================
                    ITEMS TABLE
                ================================================= */}

                <table className="dc-items-table">

                    <thead>

                        <tr>

                            <th>
                                Lot No.
                            </th>

                            <th>
                                Product Name
                            </th>

                            <th>
                                Qty.
                            </th>

                            <th>
                                Weight
                            </th>

                            <th>
                                Location
                            </th>

                        </tr>

                    </thead>


                    <tbody>

                        {items.length > 0 ? (

                            items.map(
                                (
                                    item,
                                    index
                                ) => (

                                    <tr
                                        key={
                                            item.id ||
                                            index
                                        }
                                    >

                                        <td>

                                            {
                                                item.lot_no ||
                                                "-"
                                            }

                                        </td>


                                        <td>

                                            {
                                                item.description ||
                                                item.product_mark ||
                                                "Potato"
                                            }

                                        </td>


                                        <td>

                                            {
                                                numberFormat(
                                                    item.bags_quantity
                                                )
                                            }

                                        </td>


                                        <td>

                                            {
                                                numberFormat(
                                                    item.weight_kg
                                                )
                                            }

                                        </td>


                                        <td>

                                            <div className="dc-location">

                                                {
                                                    item.location ||
                                                    "-"
                                                }


                                                {
                                                    item.chamber_no && (

                                                        <small>

                                                            {
                                                                item.chamber_no
                                                            }

                                                        </small>

                                                    )
                                                }

                                            </div>

                                        </td>

                                    </tr>

                                )

                            )

                        ) : (

                            <tr>

                                <td>
                                    -
                                </td>

                                <td>
                                    Potato
                                </td>

                                <td>
                                    0
                                </td>

                                <td>
                                    0
                                </td>

                                <td>
                                    -
                                </td>

                            </tr>

                        )}


                        {/* EMPTY ROWS */}

                        {Array.from({

                            length:
                                Math.max(
                                    0,
                                    5 - items.length
                                )

                        }).map(
                            (
                                _,
                                index
                            ) => (

                                <tr
                                    key={
                                        `empty-${index}`
                                    }
                                    className="dc-empty-row"
                                >

                                    <td></td>

                                    <td></td>

                                    <td></td>

                                    <td></td>

                                    <td></td>

                                </tr>

                            )
                        )}


                        {/* TOTAL */}

                        <tr className="dc-total-row">

                            <td
                                colSpan="2"
                            >

                                Total

                            </td>

                            <td>

                                {
                                    numberFormat(
                                        totalBags
                                    )
                                }

                            </td>

                            <td>

                                {
                                    numberFormat(
                                        totalWeight
                                    )
                                }

                            </td>

                            <td></td>

                        </tr>

                    </tbody>

                </table>


                {/* =================================================
                    REMARKS
                ================================================= */}

                <div className="dc-remarks">

                    <div className="dc-section-label">
                        Remarks
                    </div>

                    <div className="dc-remarks-content">

                        {
                            challan.remarks ||
                            ""
                        }

                    </div>

                </div>


                {/* =================================================
                    LORRY WEIGHT
                ================================================= */}

                <div className="dc-weight-table">


                    <div className="dc-weight-box">

                        <span>
                            Full Lorry Weight
                        </span>

                        <strong>

                            {
                                numberFormat(
                                    challan.full_lorry_weight_kg
                                )
                            }

                            {" "}KG

                        </strong>

                    </div>


                    <div className="dc-weight-box">

                        <span>
                            Empty Lorry Weight
                        </span>

                        <strong>

                            {
                                numberFormat(
                                    challan.empty_lorry_weight_kg
                                )
                            }

                            {" "}KG

                        </strong>

                    </div>


                    <div className="dc-weight-box">

                        <span>
                            Net Weight
                        </span>

                        <strong>

                            {
                                numberFormat(
                                    netWeight
                                )
                            }

                            {" "}KG

                        </strong>

                    </div>

                </div>


                {/* =================================================
                    DECLARATION
                ================================================= */}

                <div className="dc-declaration">

                    <div className="dc-declaration-text">

                        Received the above mentioned goods previously
                        stored with you in the same quantity & condition.

                    </div>


                    <div className="dc-company-signature">

                        <strong>
                            For {COMPANY_NAME}
                        </strong>

                    </div>

                </div>


                {/* =================================================
                    SIGNATURES
                ================================================= */}

                <div className="dc-signature-row">


                    <div className="dc-signature-box">

                        <span>
                            Customer's Representative
                        </span>

                    </div>


                    <div className="dc-signature-box">

                        <span>
                            Store Incharge
                        </span>

                    </div>

                </div>


            </div>

        </div>

    );

};


export default DeliveryChallanView;